import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import {
  login, verifyWebLoginOtp, sendWebLoginEmailOtp,
  forgetPassword, verifyForgotPasswordOtp, resendForgotPasswordOtp, resetPassword,
} from "../../services/authService";
import { useAuth } from "../../context/AuthContext";
import Input from "../../components/common/Input";
import Button from "../../components/common/Button";
import Switch from "../../components/common/Switch";
import LanguageSwitcher from "../../components/common/LanguageSwitcher";
import { APP_NAME } from "../../utils/constants";
import { color, radius, shadow } from "../../styles/theme";

// Matches mobile's isEmailValid (src/Component/FormHandler/Utils.js) exactly.
const EMAIL_REGEX = /^[A-Za-z0-9](?:[A-Za-z0-9._%+-]{0,63})@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;
const isEmailValid = (email) => email.length <= 254 && EMAIL_REGEX.test(email);

// Matches mobile's ResetPassword screen exactly: 8+ chars, 1 uppercase, 1 special char.
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.{8,})/;

const getErrorMessage = (err, fallback) => {
  const respData = err.response?.data;
  return (typeof respData === "string" ? respData : null) || respData?.Message || respData?.message || fallback;
};

const Login = () => {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const { login: setAuth } = useAuth();

  const [email, setEmail] = useState(() => localStorage.getItem("ps_remember_email") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem("ps_remember_email"));
  const [loading, setLoading] = useState(false);

  // 'login' | 'login-otp' | 'forgot-email' | 'forgot-otp' | 'forgot-reset'
  const [screen, setScreen] = useState("login");
  const [userId, setUserId] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [resending, setResending] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);
  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  // Forgot-password state
  const [forgotEmail, setForgotEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const resetOtpBoxes = () => {
    setOtp(["", "", "", ""]);
    setTimeout(() => otpRefs[0].current?.focus(), 0);
  };

  const backToLogin = () => {
    setScreen("login");
    setUserId("");
    setOtp(["", "", "", ""]);
    setForgotEmail("");
    setNewPassword("");
    setConfirmNewPassword("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (rememberMe) {
      localStorage.setItem("ps_remember_email", email);
    } else {
      localStorage.removeItem("ps_remember_email");
    }

    try {
      const res = await login(email, password, true);
      const data = res.data;

      if (data?.Status === "OTP_REQUIRED") {
        setUserId(data.UserId);
        setScreen("login-otp");
        toast.info(data.Message || t("login.toasts.otpSentToMobile"));
        return;
      }

      const authData = data?.Result?.AuthData;
      const authToken = authData?.TokenInfo?.Token;

      if (!authToken) throw new Error(t("login.toasts.loginFailed"));

      setAuth(authData, authToken);
      toast.success(t("login.toasts.loginSuccessful"));
      navigate("/dashboard");
    } catch (err) {
      toast.error(getErrorMessage(err, err.message || t("login.toasts.invalidCredentials")));
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      await login(email, password, true);
      resetOtpBoxes();
      toast.info(t("login.toasts.otpResentToMobile"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("login.toasts.resendOtpFailed")));
    } finally {
      setResending(false);
    }
  };

  const handleSendEmailOtp = async () => {
    setResending(true);
    try {
      await sendWebLoginEmailOtp(userId);
      resetOtpBoxes();
      toast.info(t("login.toasts.otpSentToEmail"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("login.toasts.sendEmailOtpFailed")));
    } finally {
      setResending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (!pasted) return;
    const newOtp = ["", "", "", ""];
    for (let i = 0; i < pasted.length; i++) newOtp[i] = pasted[i];
    setOtp(newOtp);
    const focusIdx = Math.min(pasted.length, 3);
    otpRefs[focusIdx].current?.focus();
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== 4) {
      toast.error(t("login.toasts.enter4DigitOtp"));
      return;
    }
    setLoading(true);
    try {
      const res = await verifyWebLoginOtp(userId, otpValue, trustDevice);
      const authData = res.data?.Result?.AuthData;
      const authToken = authData?.TokenInfo?.Token;

      if (!authToken) throw new Error(t("login.toasts.verificationFailed"));

      if (trustDevice) {
        const trusted = JSON.parse(localStorage.getItem("ps_trusted_devices") || "[]");
        if (!trusted.includes(userId)) trusted.push(userId);
        localStorage.setItem("ps_trusted_devices", JSON.stringify(trusted));
      }

      setAuth(authData, authToken);
      toast.success(t("login.toasts.loginSuccessful"));
      navigate("/dashboard");
    } catch (err) {
      toast.error(getErrorMessage(err, t("login.toasts.otpVerificationFailed")));
    } finally {
      setLoading(false);
    }
  };

  // --- Forgot password ---

  const handleForgotEmailSubmit = async (e) => {
    e.preventDefault();
    const normalized = forgotEmail.trim();
    if (!isEmailValid(normalized)) {
      toast.error(t("login.toasts.enterValidEmail"));
      return;
    }
    setLoading(true);
    try {
      const res = await forgetPassword(normalized);
      const returnedUserId = res.data?.Result;
      if (!returnedUserId) throw new Error(t("login.toasts.somethingWentWrong"));
      setUserId(returnedUserId);
      setForgotEmail(normalized);
      resetOtpBoxes();
      setScreen("forgot-otp");
      toast.info(res.data?.Message || t("login.toasts.otpSentToEmail"));
    } catch (err) {
      toast.error(getErrorMessage(err, err.message || t("login.toasts.resetCodeFailed")));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotOtpVerify = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== 4) {
      toast.error(t("login.toasts.enterAllOtpDigits"));
      return;
    }
    setLoading(true);
    try {
      await verifyForgotPasswordOtp(otpValue, userId);
      setScreen("forgot-reset");
    } catch (err) {
      toast.error(getErrorMessage(err, t("login.toasts.invalidOrExpiredCode")));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotResend = async () => {
    setResending(true);
    try {
      await resendForgotPasswordOtp(forgotEmail);
      resetOtpBoxes();
      toast.info(t("login.toasts.codeResent"));
    } catch (err) {
      toast.error(getErrorMessage(err, t("login.toasts.resendCodeFailed")));
    } finally {
      setResending(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword) {
      toast.error(t("login.toasts.enterNewPassword"));
      return;
    }
    if (!PASSWORD_REGEX.test(newPassword)) {
      toast.error(t("login.toasts.passwordRequirements"));
      return;
    }
    if (!confirmNewPassword) {
      toast.error(t("login.toasts.confirmNewPassword"));
      return;
    }
    if (confirmNewPassword !== newPassword) {
      toast.error(t("login.toasts.passwordsDontMatch"));
      return;
    }
    setLoading(true);
    try {
      const res = await resetPassword(userId, newPassword, confirmNewPassword);
      toast.success(res.data?.Message || t("login.toasts.resetSuccess"));
      backToLogin();
    } catch (err) {
      toast.error(getErrorMessage(err, t("login.toasts.resetFailed")));
    } finally {
      setLoading(false);
    }
  };

  const subtitle = {
    login: t("login.subtitle.login"),
    "login-otp": t("login.subtitle.loginOtp"),
    "forgot-email": t("login.subtitle.forgotEmail"),
    "forgot-otp": t("login.subtitle.forgotOtp"),
    "forgot-reset": t("login.subtitle.forgotReset"),
  }[screen];

  const title = screen === "forgot-email" || screen === "forgot-otp"
    ? t("login.forgotPasswordTitle")
    : screen === "forgot-reset"
      ? t("login.createNewPasswordTitle")
      : APP_NAME;

  return (
    <div style={s.page}>
      <div style={s.bgImage} />
      <div style={s.bgScrim} />
      <div style={s.langSwitcherWrap}>
        <LanguageSwitcher />
      </div>
      <div className="modal-pop" style={s.card}>
        {screen === "login" || screen === "login-otp" ? (
          <img src="/PS_favicon.png" alt="" style={s.logo} />
        ) : null}
        <h2 style={s.title}>{title}</h2>
        <p style={s.subtitle}>{subtitle}</p>

        {screen === "login-otp" && (
          <form onSubmit={handleVerifyOtp}>
            <div style={s.otpContainer}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={otpRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  onPaste={i === 0 ? handleOtpPaste : undefined}
                  className="input"
                  style={s.otpInput}
                  autoFocus={i === 0}
                />
              ))}
            </div>
            <Button type="submit" disabled={loading} style={{ width: "100%", marginTop: 18 }}>
              {loading ? t("login.verifying") : t("login.verify")}
            </Button>

            <div style={s.trustRow}>
              <Switch checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} />
              <span style={s.trustText}>{t("login.trustDevice")}</span>
            </div>

            <div style={s.otpFooter}>
              <button type="button" onClick={handleResendOtp} disabled={resending} style={s.resendBtn}>
                {resending ? t("login.sendingOtp") : t("login.resendOtp")}
              </button>
              <button type="button" onClick={handleSendEmailOtp} disabled={resending} style={s.resendBtn}>
                {resending ? t("login.sendingOtp") : t("login.getOtpOnEmail")}
              </button>
            </div>
            <button type="button" onClick={backToLogin} className="btn btn-ghost btn-sm" style={s.backBtn}>
              <ArrowLeft size={13} strokeWidth={2.25} /> {t("login.backToLogin")}
            </button>
          </form>
        )}

        {screen === "login" && (
          <form onSubmit={handleSubmit}>
            <Input label={t("login.emailLabel")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("login.emailPlaceholder")} required />
            <div style={{ position: "relative" }}>
              <Input
                label={t("login.passwordLabel")}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("login.passwordPlaceholder")}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={s.eyeBtn}
                tabIndex={-1}
                aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
              >
                {showPassword ? <EyeOff size={17} strokeWidth={2} /> : <Eye size={17} strokeWidth={2} />}
              </button>
            </div>

            <div style={s.rememberRow}>
              <Switch checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
              <span style={s.rememberText}>{t("login.rememberMe")}</span>
            </div>

            <Button type="submit" disabled={loading} style={{ width: "100%", marginTop: 10 }}>
              {loading ? t("login.signingIn") : t("login.signIn")}
            </Button>

            <button
              type="button"
              onClick={() => { setForgotEmail(email); setScreen("forgot-email"); }}
              style={s.forgotLink}
            >
              {t("login.forgotPasswordLink")}
            </button>
          </form>
        )}

        {screen === "forgot-email" && (
          <form onSubmit={handleForgotEmailSubmit}>
            <Input label={t("login.emailLabel")} type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder={t("login.emailPlaceholder")} required autoFocus />
            <Button type="submit" disabled={loading} style={{ width: "100%", marginTop: 10 }}>
              {loading ? t("login.sending") : t("login.continue")}
            </Button>
            <button type="button" onClick={backToLogin} className="btn btn-ghost btn-sm" style={s.backBtn}>
              <ArrowLeft size={13} strokeWidth={2.25} /> {t("login.backToLogin")}
            </button>
          </form>
        )}

        {screen === "forgot-otp" && (
          <form onSubmit={handleForgotOtpVerify}>
            <div style={s.otpContainer}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={otpRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  onPaste={i === 0 ? handleOtpPaste : undefined}
                  className="input"
                  style={s.otpInput}
                  autoFocus={i === 0}
                />
              ))}
            </div>
            <Button type="submit" disabled={loading} style={{ width: "100%", marginTop: 18 }}>
              {loading ? t("login.verifying") : t("login.verify")}
            </Button>

            <div style={{ ...s.otpFooter, justifyContent: "center" }}>
              <button type="button" onClick={handleForgotResend} disabled={resending} style={s.resendBtn}>
                {resending ? t("login.sending") : t("login.resendCode")}
              </button>
            </div>
            <button type="button" onClick={() => setScreen("forgot-email")} className="btn btn-ghost btn-sm" style={s.backBtn}>
              <ArrowLeft size={13} strokeWidth={2.25} /> {t("login.back")}
            </button>
          </form>
        )}

        {screen === "forgot-reset" && (
          <form onSubmit={handleResetPasswordSubmit}>
            <div style={{ position: "relative" }}>
              <Input
                label={t("login.newPasswordLabel")}
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <button type="button" onClick={() => setShowNewPassword((v) => !v)} style={s.eyeBtn} tabIndex={-1} aria-label={t("login.togglePasswordVisibility")}>
                {showNewPassword ? <EyeOff size={17} strokeWidth={2} /> : <Eye size={17} strokeWidth={2} />}
              </button>
            </div>
            <div style={{ position: "relative" }}>
              <Input
                label={t("login.confirmPasswordLabel")}
                type={showConfirmPassword ? "text" : "password"}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <button type="button" onClick={() => setShowConfirmPassword((v) => !v)} style={s.eyeBtn} tabIndex={-1} aria-label={t("login.togglePasswordVisibility")}>
                {showConfirmPassword ? <EyeOff size={17} strokeWidth={2} /> : <Eye size={17} strokeWidth={2} />}
              </button>
            </div>
            <p style={s.hint}>{t("login.passwordHint")}</p>
            <Button type="submit" disabled={loading} style={{ width: "100%", marginTop: 4 }}>
              {loading ? t("login.saving") : t("login.resetPassword")}
            </Button>
            <button type="button" onClick={backToLogin} className="btn btn-ghost btn-sm" style={s.backBtn}>
              <ArrowLeft size={13} strokeWidth={2.25} /> {t("login.backToLogin")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" },
  bgImage: {
    position: "absolute", inset: "-10px", backgroundImage: "url(/PS.png)", backgroundSize: "cover",
    backgroundPosition: "center", filter: "blur(3px)", zIndex: 0,
  },
  bgScrim: { position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(35,8,10,0.12) 0%, rgba(25,6,10,0.22) 55%, rgba(20,5,8,0.4) 100%)", zIndex: 0 },
  langSwitcherWrap: { position: "absolute", top: 20, right: 24, zIndex: 2 },
  card: {
    position: "relative", zIndex: 1, background: "rgba(255,255,255,0.97)", padding: "40px 40px 36px",
    borderRadius: radius.xl, boxShadow: shadow.lg, width: "100%", maxWidth: 400, backdropFilter: "blur(10px)",
  },
  logo: { width: 44, height: 44, borderRadius: radius.sm, display: "block", margin: "0 auto 14px" },
  title: { margin: "0 0 4px", fontSize: 21, fontWeight: 800, color: color.textPrimary, textAlign: "center", letterSpacing: -0.3 },
  subtitle: { margin: "0 0 26px", color: color.textMuted, textAlign: "center", fontSize: 13.5 },
  eyeBtn: {
    position: "absolute", right: 10, top: 33, background: "none", border: "none", cursor: "pointer",
    padding: "4px 6px", lineHeight: 1, color: color.gray500, display: "flex",
  },
  rememberRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
  rememberText: { fontSize: 13, color: color.gray700, fontWeight: 500 },
  forgotLink: {
    display: "block", margin: "16px auto 0", background: "none", border: "none",
    color: color.primary600, fontWeight: 600, fontSize: 12.5, cursor: "pointer", textAlign: "center",
  },
  trustRow: { display: "flex", alignItems: "center", gap: 10, marginTop: 16, justifyContent: "center" },
  trustText: { fontSize: 12, color: color.textSecondary },
  otpContainer: { display: "flex", justifyContent: "center", gap: 12, margin: "22px 0 8px" },
  otpInput: { width: 48, height: 54, textAlign: "center", fontSize: 22, fontWeight: 700, caretColor: color.primary500 },
  otpFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 },
  resendBtn: { background: "none", border: "none", color: color.textMuted, cursor: "pointer", fontSize: 12.5, fontWeight: 600, textDecoration: "underline" },
  backBtn: { display: "flex", margin: "14px auto 0" },
  hint: { fontSize: 11.5, color: color.textMuted, margin: "-8px 0 14px", lineHeight: 1.5 },
};

export default Login;
